/* eslint-disable react-refresh/only-export-components -- context-module pattern: the provider ships with its hook and size constants; splitting them into separate files adds churn for a three-value module with no independent lifecycle. */
import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** Default rows per page in a breakdown table. Tuned so a tall table (Workers,
 *  Activities) stays scannable — the rows that matter sit on top under the
 *  active sort, so later pages hold the tail. */
export const DEFAULT_PAGE_SIZE = 10;

export const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

interface IPageSizeContextValue {
  pageSize: number;
  setPageSize: (size: number) => void;
}

/** Default lets `usePagination` work outside a provider (tests, one-off
 *  tables) at the fixed default size; the setter is a no-op there. */
const PageSizeContext = createContext<IPageSizeContextValue>({
  pageSize: DEFAULT_PAGE_SIZE,
  setPageSize: () => {},
});

/**
 * One page size shared by every breakdown table. Lives on the Breakdown card
 * (not per panel) so the choice survives tab switches — panels mount and
 * unmount as tabs change, so panel-local state would silently reset.
 */
export function PageSizeProvider({ children }: { children: ReactNode }) {
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const value = useMemo(() => ({ pageSize, setPageSize }), [pageSize]);
  return <PageSizeContext.Provider value={value}>{children}</PageSizeContext.Provider>;
}

export function usePageSize() {
  return useContext(PageSizeContext);
}
